import { z } from 'zod';

import { supabase } from '@/integrations/supabase/client';

export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/\d/, 'Password must contain at least one number');

export interface PasswordStrength {
  strength: number;
  label: string;
  color: string;
}

export function getPasswordStrength(password: string): PasswordStrength {
  let strength = 0;
  if (password.length >= 12) {
    strength += 25;
  }
  if (password.length >= 16) {
    strength += 15;
  }
  if (/[A-Z]/.test(password)) {
    strength += 20;
  }
  if (/[a-z]/.test(password)) {
    strength += 20;
  }
  if (/\d/.test(password)) {
    strength += 20;
  }
  if (/[^\dA-Za-z]/.test(password)) {
    strength += 20;
  }

  if (strength < 40) {
    return { strength, label: 'Weak', color: 'bg-red-500' };
  }
  if (strength < 70) {
    return { strength, label: 'Fair', color: 'bg-orange-500' };
  }
  if (strength < 90) {
    return { strength, label: 'Good', color: 'bg-yellow-500' };
  }
  return { strength, label: 'Strong', color: 'bg-green-500' };
}

export interface PasswordRequirement {
  met: boolean;
  text: string;
}

export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { met: password.length >= 12, text: 'At least 12 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /\d/.test(password), text: 'One number' },
  ];
}

/**
 * Verify the user's current credentials and update to a new password.
 * Optionally elevates to AAL2 via TOTP if MFA is enabled.
 *
 * The `password` properties below are required by the Supabase Auth SDK —
 * they carry runtime user input, not hard-coded secrets.
 */
export async function verifyAndUpdatePassword(
  currentCredential: string,
  newCredential: string,
  totpCode?: string,
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user?.email) {
    throw new Error('User not found');
  }

  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentCredential, // NOSONAR — runtime value, not a hard-coded secret
  });

  if (signInError) {
    throw new Error('Current password is incorrect');
  }

  // Elevate to AAL2 if MFA is enabled (required for password changes)
  if (totpCode) {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const verifiedFactor = factors?.totp?.find((f) => f.status === 'verified');
    if (verifiedFactor) {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: verifiedFactor.id,
      });
      if (challengeError) {
        throw new Error('Failed to create MFA challenge');
      }
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: verifiedFactor.id,
        challengeId: challenge.id,
        code: totpCode,
      });
      if (verifyError) {
        throw new Error('Invalid authenticator code');
      }
    }
  }

  // password_set marks the account as holding its own credential, so an
  // invitation later offers Accept instead of the first-time setup form.
  const { error: updateError } = await supabase.auth.updateUser({
    password: newCredential, // NOSONAR — runtime value, not a hard-coded secret
    data: { password_set: true },
  });

  if (updateError) {
    throw updateError;
  }
}

export function isWeakPasswordError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'AuthWeakPasswordError' || error.message.includes('weak'))
  );
}

/**
 * Formats a user-friendly error message for weak password errors from Supabase
 */
export function formatWeakPasswordError(error: unknown): string {
  const err = error as Record<string, unknown> | null | undefined;
  // Check if this is a weak password error
  if (err?.name === 'AuthWeakPasswordError' || (err?.message as string)?.includes('weak')) {
    const weakPassword = err?.weak_password as Record<string, unknown> | undefined;
    const reasons: string[] = (weakPassword?.reasons as string[]) || [];

    if (reasons.length > 0) {
      const formattedReasons = reasons.map((reason: string) => {
        // Convert snake_case reasons to readable text
        return reason
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      });

      return `This password is too weak. ${formattedReasons.join('. ')}.`;
    }

    return 'This password is too weak. Please choose a stronger, more unique password.';
  }

  // Return the original error message if not a weak password error
  return (err?.message as string) || 'An error occurred';
}
